import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, DoorOpen } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { RoomCard } from "../components/RoomCard";
import { CtaBlock } from "../components/CtaBlock";
import { findPhoto } from "../lib/photos";
import { useSeo } from "../lib/seo";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Rooms() {
  const [rooms, setRooms] = useState([]);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [r, p] = await Promise.all([
          axios.get(`${API}/rooms`).then((r) => r.data),
          axios.get(`${API}/photos`).then((r) => r.data),
        ]);
        if (!alive) return;
        setRooms(r || []);
        setPhotos(p.photos || []);
      } catch (e) {
        console.error("rooms fetch fail", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const heroPhoto = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-suite-interior"),
    [photos],
  );

  const canonical = "https://westbridgehotels.com/rooms";
  useSeo({
    title: "Rooms & Suites — Westbridge Inn & Suites, Centerville, Iowa",
    description:
      "Six honest room categories in Centerville, Iowa — Double Queen, King, Accessible King, King Jacuzzi Suite, Construction Crew Rooms, and Exterior Access. Book direct.",
    canonical,
    jsonLd: [buildBreadcrumbLd([{ label: "Rooms", to: "/rooms" }])],
  });

  return (
    <main id="main">
      <PageHero
        eyebrow="Rooms & Suites"
        title="Fifty-two rooms. Six honest layouts."
        intro="Every room at Westbridge Inn & Suites has an in-room refrigerator, microwave, coffee maker, and a 55-inch 4K TV. What changes is the layout — a Double Queen for the family, a King Jacuzzi Suite for the anniversary, an Exterior Access room for the 5 a.m. departure. Pick the one that fits the trip."
        photo={heroPhoto}
        breadcrumbs={[{ label: "Rooms", to: "/rooms" }]}
        testId="rooms-hero"
      />

      {/* Interior vs Exterior explainer */}
      <section className="bg-cream py-16 md:py-24" data-testid="rooms-corridor-teaser">
        <div className="container-app grid md:grid-cols-2 gap-6 md:gap-10">
          <div className="bg-cream-50 border border-tan p-8 md:p-10">
            <Building2 size={22} className="text-brass mb-4" />
            <h2 className="font-display text-2xl md:text-3xl text-forest-900 mb-3">
              22 interior-corridor rooms
            </h2>
            <p className="text-ink/80 leading-relaxed">
              Enter through the lobby, walk a climate-controlled hallway to your door. Steps from the pool and the breakfast room. Quieter for light sleepers, warmer in winter.
            </p>
          </div>
          <div className="bg-forest-900 text-cream p-8 md:p-10">
            <DoorOpen size={22} className="text-brass-300 mb-4" />
            <h2 className="font-display text-2xl md:text-3xl text-cream mb-3">
              30 exterior-access rooms
            </h2>
            <p className="text-cream/80 leading-relaxed">
              Park directly at your door on our east wing. Truck-friendly, pet-friendly, and ideal for late arrivals or dawn departures.
            </p>
          </div>
        </div>
        <div className="container-app mt-10">
          <Link
            to="/rooms/interior-vs-exterior"
            className="inline-flex items-center gap-2 text-forest-900 brass-underline text-sm md:text-base"
            data-testid="rooms-interior-exterior-link"
          >
            Read the full Interior vs Exterior guide <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>

      {/* Room grid */}
      <section className="bg-cream-100 py-20 md:py-28 paper-grain" data-testid="rooms-grid">
        <div className="container-app relative">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">All six categories</p>
            <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-tight">
              Every layout, every detail.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {rooms.map((r) => (
              <RoomCard key={r.slug} room={r} photo={findPhoto(photos, r.photo_slug)} />
            ))}
          </div>
        </div>
      </section>

      <CtaBlock
        eyebrow="Book Direct & Save"
        title="One call. One rate. No surprises."
        body="Our booking page is the same rate the front desk sees. Call for driver rates, crew blocks, or anniversary suites."
      />
    </main>
  );
}
